// src/ui/MediaViewer.jsx
//
// 🟢 FULLSCREEN MEDIA VIEWER — one component for all three cases:
//      • single photo   → zoom / pan
//      • album ("batch") → arrows, keyboard, swipe, filmstrip, "3 of 7"
//      • video          → autoplaying player with native controls
//
// Chrome (per the spec): download + close on the right, and the SENDER'S NAME with
// the sent/received time dead centre. Everything is sized for a desktop window —
// the stage takes the full viewport minus the two bars, so a 4000px photo actually
// fills a 27" monitor instead of sitting in a 340px box.
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { X, Download, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Play } from './icons';
import { fullTimeLabel } from './format';

const MAX_ZOOM = 6;
const MIN_ZOOM = 1;

function extFor(item) {
  if (item.kind === 'video') return 'mp4';
  const m = /image\/(\w+)/.exec(item.mime || '');
  return m ? (m[1] === 'jpeg' ? 'jpg' : m[1]) : 'jpg';
}

/** Save the DECRYPTED blob, not the ciphertext URL — the object URL already holds
 *  plaintext bytes, so an <a download> is all it takes. */
async function downloadItem(item, who, when) {
  /* 🟥 A DOCUMENT SAVES THE FILE, NOT A PICTURE OF ITS FIRST PAGE. The stage
     shows `url` — for a PDF that is the rendered page-one PNG — but downloading
     that would hand somebody a screenshot of the contract instead of the
     contract. When the item carries a real file, `downloadUrl` and the sender's
     own `fileName` win, and the generated allo-name is only used for media that
     never had a filename of its own. */
  const href = item?.downloadUrl || item?.url;
  if (!href) return;
  const stamp = new Date(when || Date.now()).toISOString().slice(0, 19).replace(/[:T]/g, '-');
  const safeWho = (who || 'allo').replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '');
  const name = item?.fileName || `allo-${safeWho}-${stamp}.${extFor(item)}`;
  try {
    const a = document.createElement('a');
    a.href = href;
    a.download = name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } catch { /* a blocked download is not worth a crash */ }
}

export default function MediaViewer({ view, onClose }) {
  const items = view?.items || [];
  const [index, setIndex] = useState(view?.index || 0);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [chrome, setChrome] = useState(true); // bars visible
  const dragRef = useRef(null);
  const touchRef = useRef(null);
  const hideTimer = useRef(null);

  const count = items.length;
  const item = items[Math.min(index, Math.max(0, count - 1))] || null;
  const many = count > 1;

  const resetView = useCallback(() => { setZoom(1); setPan({ x: 0, y: 0 }); }, []);

  const go = useCallback((delta) => {
    if (!many) return;
    setIndex((i) => (i + delta + count) % count);
    resetView();
  }, [many, count, resetView]);

  // reset when the caller opens a different message
  useEffect(() => { setIndex(view?.index || 0); resetView(); }, [view, resetView]);

  // keyboard: ← → navigate · Esc close · +/- zoom · 0 reset · d download
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key === 'ArrowRight') { go(1); return; }
      if (e.key === 'ArrowLeft') { go(-1); return; }
      if (e.key === '+' || e.key === '=') { setZoom((z) => Math.min(MAX_ZOOM, z * 1.4)); return; }
      if (e.key === '-') { setZoom((z) => Math.max(MIN_ZOOM, z / 1.4)); return; }
      if (e.key === '0') { resetView(); return; }
      if (e.key.toLowerCase() === 'd') downloadItem(item, view?.senderName, view?.timestamp);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, go, item, view, resetView]);

  // auto-hide the chrome while the pointer is still, like a real photo viewer
  const wakeChrome = useCallback(() => {
    setChrome(true);
    clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setChrome(false), 3200);
  }, []);
  useEffect(() => { wakeChrome(); return () => clearTimeout(hideTimer.current); }, [wakeChrome, index]);

  if (!view || !item) return null;

  const isVideo = item.kind === 'video';
  const zoomed = zoom > 1.01;

  const onWheel = (e) => {
    if (isVideo) return;
    e.preventDefault();
    setZoom((z) => {
      const next = Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z * (e.deltaY < 0 ? 1.16 : 1 / 1.16)));
      if (next <= 1.01) setPan({ x: 0, y: 0 });
      return next;
    });
  };

  const onPointerDown = (e) => {
    if (!zoomed) return;
    dragRef.current = { x: e.clientX, y: e.clientY, x0: pan.x, y0: pan.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    wakeChrome();
    if (!dragRef.current) return;
    setPan({ x: dragRef.current.x0 + (e.clientX - dragRef.current.x), y: dragRef.current.y0 + (e.clientY - dragRef.current.y) });
  };
  const onPointerUp = () => { dragRef.current = null; };

  // touch swipe between album items (only when not zoomed in)
  const onTouchStart = (e) => { touchRef.current = e.touches[0]?.clientX ?? null; };
  const onTouchEnd = (e) => {
    if (touchRef.current == null || zoomed) return;
    const dx = (e.changedTouches[0]?.clientX ?? 0) - touchRef.current;
    if (Math.abs(dx) > 60) go(dx < 0 ? 1 : -1);
    touchRef.current = null;
  };

  return (
    <div className={`mv ${chrome ? '' : 'mv-quiet'}`} onMouseMove={wakeChrome}>
      <div className="mv-scrim" onClick={onClose} />

      {/* ── top bar ── */}
      <div className="mv-bar mv-top">
        <div className="mv-side mv-left">
          {many && <span className="mv-counter">{index + 1} <em>/</em> {count}</span>}
        </div>

        {/* centre: WHO sent it and WHEN — the spec's requirement */}
        <div className="mv-who">
          <div className="mv-who-name">{view.senderName || 'Allo user'}</div>
          <div className="mv-who-time">
            {view.mine ? 'Sent' : 'Received'} · {fullTimeLabel(view.timestamp)}
          </div>
        </div>

        <div className="mv-side mv-right">
          {!isVideo && (
            <>
              <button className="mv-btn" title="Zoom out (−)" onClick={() => setZoom((z) => Math.max(MIN_ZOOM, z / 1.4))} disabled={!zoomed}><ZoomOut /></button>
              <span className="mv-zoom">{Math.round(zoom * 100)}%</span>
              <button className="mv-btn" title="Zoom in (+)" onClick={() => setZoom((z) => Math.min(MAX_ZOOM, z * 1.4))}><ZoomIn /></button>
            </>
          )}
          <button className="mv-btn" title="Download (D)" onClick={() => downloadItem(item, view.senderName, view.timestamp)}><Download /></button>
          <button className="mv-btn mv-close" title="Close (Esc)" onClick={onClose}><X width={22} height={22} /></button>
        </div>
      </div>

      {/* ── stage ── */}
      <div
        className="mv-stage"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {!item.url ? (
          <div className="mv-missing">This media is no longer available.</div>
        ) : isVideo ? (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video className="mv-media" src={item.url} controls autoPlay playsInline onClick={(e) => e.stopPropagation()} />
        ) : (
          <img
            className={`mv-media ${zoomed ? 'mv-grab' : ''}`}
            src={item.url}
            alt=""
            draggable={false}
            style={{ transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})` }}
            onClick={(e) => e.stopPropagation()}
            onDoubleClick={() => (zoomed ? resetView() : setZoom(2.4))}
          />
        )}

        {many && (
          <>
            <button className="mv-nav mv-prev" onClick={() => go(-1)} title="Previous (←)"><ChevronLeft width={30} height={30} /></button>
            <button className="mv-nav mv-next" onClick={() => go(1)} title="Next (→)"><ChevronRight width={30} height={30} /></button>
          </>
        )}
      </div>

      {/* ── caption + filmstrip ── */}
      <div className="mv-bar mv-bottom">
        {view.caption ? <div className="mv-caption">{view.caption}</div> : null}
        {many && (
          <div className="mv-strip">
            {items.map((it, i) => (
              <button
                key={i}
                className={`mv-thumb ${i === index ? 'on' : ''}`}
                onClick={() => { setIndex(i); resetView(); }}
                title={`${i + 1} of ${count}`}
              >
                {it.url
                  ? (it.kind === 'video'
                    // eslint-disable-next-line jsx-a11y/media-has-caption
                    ? <><video src={it.url} muted /><span className="mv-thumb-play"><Play width={14} height={14} /></span></>
                    : <img src={it.url} alt="" />)
                  : <span className="mv-thumb-dead" />}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
